import MoneyIcon from '@mui/icons-material/MonetizationOn'
import PeopleIcon from '@mui/icons-material/People'
import Grid2 from '@mui/material/Grid2'
import { styled } from '@mui/material/styles'
import { isEmpty } from 'ramda'
import React from 'react'

import SummaryBox from '@/app/component/atom/reports/summary-box'
import Quotas from '@/app/module/campaigns/component/item/steps/quotas/quotas'
import { CampaignTemplateType } from '@/app/module/campaigns/types'
import type { CampaignItem } from '@/app/module/campaigns/types/common'
import type { CampaignSummary } from '@/app/module/campaigns/types/summary'
import { useGetQuotas } from '@/app/module/content/hooks/use-get-quotas'

import CostOverviewTable from './components/cost-overview-table'
import MessageOverviewTable from './components/message-overview-table'
import OverviewChart, { BarChartDatum } from './components/overview-bar-chart'
import OverviewPieChart, { PieChartDatum } from './components/overview-pie-chart'
import TopupOverviewTable from './components/topup-overview-table'
import VoiceOverviewTable from './components/voice-overview-table'
import WhatsappOverviewTable from './components/whatsapp-overview-table'
import { getChartData } from './helpers'

type Props = {
  item?: CampaignItem
  itemId: number
  itemSummary?: CampaignSummary
}

const OverviewTab: React.FC<Props> = ({ item, itemId, itemSummary }) => {
  const type = item?.type

  const chart = React.useMemo(() => {
    if (!item || !type || !itemSummary || isEmpty(itemSummary)) {
      return []
    }

    switch (type) {
      case CampaignTemplateType.CATI:
      case CampaignTemplateType.SMSBlast:
      case CampaignTemplateType.VoiceSurvey:
        return getChartData(itemSummary, type)

      case CampaignTemplateType.Drip:
      case CampaignTemplateType.Reminder:
      case CampaignTemplateType.SMSSurvey:
      case CampaignTemplateType.WhatsAppSurvey:
        return getChartData(itemSummary, type)

      default:
        return []
    }
  }, [item, itemSummary, type])

  if (!item || isEmpty(item) || !itemSummary || isEmpty(itemSummary) || !type) {
    return null
  }

  const hasOverviewTables = [
    itemSummary.overallAirtimeStats,
    itemSummary.overallMessageStats,
    itemSummary.overallVoiceStats,
    itemSummary.overallWhatsappStats,
  ].some((stats) => !!stats.total)

  return (
    <React.Fragment>
      <Grid2 container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <SummaryBox icon={MoneyIcon} label="Total Cost" value={itemSummary.costHuman} />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <SummaryBox icon={PeopleIcon} label="Subscriptions" value={item.runs} />
        </Grid2>
      </Grid2>
      {hasOverviewTables && (
        <Grid2 container data-testid="overview-tables-row" spacing={2} sx={{ marginBottom: 2 }}>
          <GridItems itemSummary={itemSummary} />
        </Grid2>
      )}
      {!!item.quotas?.length && <ReadOnlyQuotas item={item} itemId={itemId} />}

      <ChartRow container spacing={2}>
        <OverviewPanel data-testid="message-delivery-overview-panel" size={{ xs: 12, md: 6 }}>
          {barChartTypes.includes(type) && <OverviewChart data={chart as BarChartDatum[]} />}
          {!barChartTypes.includes(type) && <OverviewPieChart data={chart as PieChartDatum[]} />}
        </OverviewPanel>
      </ChartRow>
    </React.Fragment>
  )
}

const ReadOnlyQuotas: React.FC<{ item: CampaignItem; itemId: number }> = ({ item, itemId }) => {
  const { data } = useGetQuotas(itemId)
  const campaignCountsByQuotaId = React.useMemo(
    () => Object.fromEntries((data?.quotas ?? []).map(({ campaigns, id }) => [id, campaigns])),
    [data?.quotas],
  )

  return <Quotas campaignCountsByQuotaId={campaignCountsByQuotaId} item={item} readOnly />
}

const barChartTypes: CampaignTemplateType[] = [
  CampaignTemplateType.Drip,
  CampaignTemplateType.Reminder,
  CampaignTemplateType.SMSSurvey,
  CampaignTemplateType.WhatsAppSurvey,
]

const GridItems: React.FC<{ itemSummary?: CampaignSummary }> = ({ itemSummary }) => {
  if (!itemSummary || isEmpty(itemSummary)) {
    return null
  }

  const { overallAirtimeStats, overallMessageStats, overallVoiceStats, overallWhatsappStats } = itemSummary
  const stats = [overallAirtimeStats, overallMessageStats, overallVoiceStats, overallWhatsappStats].filter(
    (s) => !!s.total,
  )

  return (
    <>
      {!!stats.length && (
        <Grid2 size={getGridWidth(0, 5)}>
          <CostOverviewTable data={itemSummary} />
        </Grid2>
      )}
      {!!overallMessageStats.total && (
        <Grid2 size={getGridWidth(0, stats.length)}>
          <MessageOverviewTable data={overallMessageStats} />
        </Grid2>
      )}
      {!!overallVoiceStats.total && (
        <Grid2 size={getGridWidth(0, stats.length)}>
          <VoiceOverviewTable data={overallVoiceStats} />
        </Grid2>
      )}
      {!!overallWhatsappStats.total && (
        <Grid2 size={getGridWidth(0, stats.length)}>
          <WhatsappOverviewTable data={overallWhatsappStats} />
        </Grid2>
      )}
      {!!overallAirtimeStats.total && (
        <Grid2 size={getGridWidth(0, stats.length)}>
          <TopupOverviewTable data={overallAirtimeStats} />
        </Grid2>
      )}
    </>
  )
}

const getGridWidth = (index: number, itemsCount: number) => {
  // if the count is even, then we want to have 2 items per row
  if (itemsCount % 2 === 0) {
    return {
      sm: 6,
      xs: 12,
    }
  }

  // if the count is odd, then we want to have 2 items per row except for the last item
  return {
    sm: index !== itemsCount - 1 ? 6 : 12,
    xs: 12,
  }
}

const ChartRow = styled(Grid2)(({ theme }) => ({
  alignItems: 'stretch',
  marginBottom: theme.spacing(3),
}))

const OverviewPanel = styled(Grid2)({
  display: 'flex',
  minWidth: 0,
  '& > *': {
    flex: 1,
    minWidth: 0,
  },
})

export default OverviewTab
